/**
 * ROI Verification Pledge — public trust contract
 * =================================================
 * Reads /api/roi-pledge/current and renders the live, versioned pledge.
 * Replaces hand-wavy marketing with a verifiable, dated commitment.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  ArrowLeft, ShieldCheck, FileBadge, ExternalLink, CheckCircle2, XCircle,
  Loader2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROIPledge = () => {
  const [pledge, setPledge] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, r] = await Promise.all([
          axios.get(`${API_URL}/roi-pledge/current`),
          axios.get(`${API_URL}/pilot-activation/readiness-score`),
        ]);
        setPledge(p.data);
        setReadiness(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="ROI Verification Pledge · CARTIEAI"
        description="Our written, versioned commitment: 3× annual fee or refund, open-math savings ledger, no fake adoption metrics, SOC 2 in progress."
        url="https://cartieai.com/roi-pledge"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white" data-testid="roi-pledge-home-link">
            <ArrowLeft className="w-4 h-4" /> Back to CARTIE
          </Link>
        </div>

        {loading && (
          <div className="text-sm text-slate-200 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the pledge…</div>
        )}

        {pledge && (
          <>
            <div className="mb-10">
              <Badge className="bg-emerald-500/20 text-emerald-300 mb-3">
                <FileBadge className="w-3 h-3 mr-1 inline" /> v{pledge.version} · effective {pledge.effective_date}
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="roi-pledge-title">
                {pledge.title}
              </h1>
              <p className="text-base sm:text-lg text-slate-200 max-w-3xl leading-relaxed">
                {pledge.summary}
              </p>
            </div>

            {/* Guarantees */}
            <Card className="border-emerald-300 bg-slate-900 mb-6">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-300" /> What we promise
                </CardTitle>
                <CardDescription>Each item is verifiable. Click the verification link where applicable.</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-5" data-testid="roi-pledge-guarantees">
                  {pledge.guarantees.map((g) => (
                    <li key={g.id} className="flex gap-3" data-testid={`roi-pledge-guarantee-${g.id}`}>
                      <CheckCircle2 className="w-5 h-5 text-emerald-300 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <div className="font-semibold text-white">{g.title}</div>
                        <div className="text-sm text-slate-200 mt-1 leading-relaxed">{g.detail}</div>
                        {g.verification_url && (
                          <a
                            href={g.verification_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1 mt-2"
                          >
                            Verify <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Non-promises */}
            <Card className="border-amber-500/30 bg-amber-500/10 mb-6">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-amber-300" /> What we will NOT do
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2" data-testid="roi-pledge-nonpromises">
                  {pledge.non_promises.map((np, i) => (
                    <li key={i} className="text-sm text-amber-900 flex gap-2">
                      <span className="text-amber-300">×</span>{np}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Verification endpoints */}
            <Card className="border-emerald-500/20 bg-slate-900 mb-6">
              <CardHeader>
                <CardTitle className="text-base">Verifiable endpoints (curl these)</CardTitle>
                <CardDescription>The receipts. Nothing is theatre.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 font-mono text-xs text-slate-200" data-testid="roi-pledge-endpoints">
                  {pledge.verification_endpoints.map((url) => (
                    <div key={url} className="px-2 py-1 bg-slate-800 rounded inline-block mr-2 mb-1">{url}</div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Live readiness badge */}
            {readiness && (
              <Card className="border-emerald-500/30 bg-emerald-500/10">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-emerald-300" />
                    <div className="flex-1">
                      <div className="text-sm text-slate-200">Live platform-readiness score</div>
                      <div className="text-2xl font-bold text-emerald-200 tabular-nums" data-testid="roi-pledge-readiness">
                        {readiness.score_pct}% <span className="text-sm font-normal text-slate-200">({readiness.ok_count}/{readiness.total_count} green)</span>
                      </div>
                    </div>
                    <a href="/api/pilot-activation/readiness-score" target="_blank" rel="noreferrer" className="text-xs text-emerald-300 hover:underline inline-flex items-center gap-1">
                      View JSON <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="mt-8 text-xs text-slate-200">
              Issued {pledge.issued_at} · Contact: <a href={`mailto:${pledge.contact}`} className="underline">{pledge.contact}</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ROIPledge;
